/**
 * Filter utilities for the audit logs table.
 */

import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone, parseDate } from '@internationalized/date';
import type { AuditLogFilters } from '../../types';
import { parseObjectTypeActionValue } from '../../utils/object-type-mapping';

// Constants for "all" filter values
export const ALL_USERS_VALUE = 'all';
export const ALL_OBJECT_TYPES_VALUE = 'all';

// UI constants
// Note: These represent the number of days to display in the UI (e.g., "Last 30 days").
// The actual date calculation subtracts (N - 1) days because "Last N days" includes today.
// Example: "Last 30 days" on Dec 11 = Nov 12 to Dec 11 (30 calendar days inclusive).
export const DEFAULT_DATE_RANGE_DAYS = 30;
export const LOADING_SKELETON_ROW_COUNT = 5;

// FilterValue type definition
export type FilterValue = {
  key: string;
  value: unknown;
  isDisabled?: boolean;
};

/**
 * Get the default date range (last N days, including today).
 * "Last 30 days" means 30 calendar days: today is day 1, so we subtract (N - 1).
 * Example: On Dec 11, "Last 30 days" = Nov 12 (day 30) to Dec 11 (day 1).
 */
export function getDefaultDateRange(): { start: CalendarDate; end: CalendarDate } {
  const endDate = today(getLocalTimeZone());
  const startDate = endDate.subtract({ days: DEFAULT_DATE_RANGE_DAYS - 1 });
  return { start: startDate, end: endDate };
}

/**
 * Validate and clamp date range for accounts with limited access.
 * Ensures:
 * 1. End date is not in the future
 * 2. Start date is not earlier than the day limit ago
 * 3. If the entire range is outside the allowed window, resets to default
 * 4. Prevents invalid inverted ranges (start > end)
 * 5. Handles partial date ranges (only start or only end provided)
 *
 * @param dateRange - The date range to validate
 * @param dayLimit - The maximum number of days allowed (null means unlimited)
 * @returns A new date range object with validated/clamped dates (never mutates input)
 */
export function validateDateRangeForNonPremium(
  dateRange: { start?: CalendarDate; end?: CalendarDate },
  dayLimit: number | null,
): { start?: CalendarDate; end?: CalendarDate } {
  // Accounts with unlimited access have no restrictions
  if (dayLimit == null) {
    return { start: dateRange.start, end: dateRange.end };
  }

  // If both are null, return as-is
  if (dateRange.start == null && dateRange.end == null) {
    return { start: dateRange.start, end: dateRange.end };
  }

  const todayDate = today(getLocalTimeZone());
  // "Last N days" includes today, so min allowed = today - (N - 1)
  const minAllowedDate = todayDate.subtract({ days: dayLimit - 1 });

  // Handle partial date ranges
  if (dateRange.start == null && dateRange.end != null) {
    // Only end date provided - clamp end and set start to N days before (or minAllowedDate)
    let clampedEnd = dateRange.end;
    if (clampedEnd.compare(todayDate) > 0) {
      clampedEnd = todayDate;
    }
    if (clampedEnd.compare(minAllowedDate) < 0) {
      // End is too old, reset to default
      return getDefaultDateRange();
    }
    // Set start to max days before end, but not earlier than minAllowedDate
    // "Last N days" includes both endpoints, so subtract (N - 1)
    const calculatedStart = clampedEnd.subtract({ days: dayLimit - 1 });
    const clampedStart = calculatedStart.compare(minAllowedDate) < 0 ? minAllowedDate : calculatedStart;
    return { start: clampedStart, end: clampedEnd };
  }

  if (dateRange.end == null && dateRange.start != null) {
    // Only start date provided - clamp start and set end to today
    let clampedStart = dateRange.start;
    if (clampedStart.compare(minAllowedDate) < 0) {
      clampedStart = minAllowedDate;
    }
    if (clampedStart.compare(todayDate) > 0) {
      // Start is in the future, clamp to today
      clampedStart = todayDate;
    }
    return { start: clampedStart, end: todayDate };
  }

  // Both dates provided - validate with improved order to prevent inverted ranges
  // TypeScript: We know both are not null from the checks above
  let clampedEnd: CalendarDate = dateRange.end as CalendarDate;
  let clampedStart: CalendarDate = dateRange.start as CalendarDate;

  // Step 1: Clamp end date to today (prevent future dates)
  if (clampedEnd.compare(todayDate) > 0) {
    clampedEnd = todayDate;
  }

  // Step 2: If end date is before the minimum allowed date, entire range is invalid
  // Reset to default range to avoid invalid state
  if (clampedEnd.compare(minAllowedDate) < 0) {
    return getDefaultDateRange();
  }

  // Step 3: Clamp start date to minimum allowed date
  if (clampedStart.compare(minAllowedDate) < 0) {
    clampedStart = minAllowedDate;
  }

  // Step 4: Ensure start is not after end (prevents inverted ranges)
  // This check happens after all clamping to catch any edge cases
  if (clampedStart.compare(clampedEnd) > 0) {
    clampedStart = clampedEnd;
  }

  // Return a new object with clamped values instead of mutating the input
  return { start: clampedStart, end: clampedEnd };
}

/**
 * Build API filters from filter values array.
 */
export function buildFiltersFromValues(filterValues: FilterValue[], dayLimit: number | null): Partial<AuditLogFilters> {
  const newFilters: Partial<AuditLogFilters> = { cursor: undefined };

  for (const filter of filterValues) {
    const filterValue = filter.value;
    if (filterValue === '' || filterValue == null) continue;

    switch (filter.key) {
      case 'user_id': {
        // Clear filter when "all" is selected
        if (typeof filterValue === 'string' && filterValue === ALL_USERS_VALUE) {
          newFilters.user_id = undefined;
          break;
        }
        // Safe to convert - filterValue is non-null/non-undefined at this point
        const filterValueStr: string = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsedUserId = Number.parseInt(filterValueStr, 10);
        if (!Number.isNaN(parsedUserId)) {
          newFilters.user_id = parsedUserId;
        }
        break;
      }
      case 'ip_addr':
        newFilters.ip_addr = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'object_type': {
        // Always clear attribute_name when object_type changes (each type has different attributes)
        newFilters.attribute_name = undefined;

        // Clear filter when "all" is selected (also clear action and object_id since they depend on object_type)
        if (typeof filterValue === 'string' && filterValue === ALL_OBJECT_TYPES_VALUE) {
          newFilters.object_type = undefined;
          newFilters.action = undefined;
          newFilters.object_id = undefined;
          break;
        }

        // Handle combined format "ObjectType:action" (e.g., "User:updated")
        const filterValueStr = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsed = parseObjectTypeActionValue(filterValueStr);
        if (parsed != null) {
          newFilters.object_type = parsed.objectType;
          newFilters.action = parsed.action;
        } else {
          newFilters.object_type = filterValueStr;
        }
        break;
      }
      case 'action': {
        // Handle action filter separately
        if (filterValue === '' || filterValue == null) {
          newFilters.action = undefined;
        } else {
          newFilters.action = typeof filterValue === 'string' ? filterValue : String(filterValue);
        }
        break;
      }
      case 'object_id':
        newFilters.object_id = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'attribute_name':
        newFilters.attribute_name = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'date_range': {
        // Handle date range filter - value is { start?: CalendarDate, end?: CalendarDate }
        let dateRange = filterValue as { start?: CalendarDate; end?: CalendarDate };
        // Validate and clamp date range for accounts with limited access
        dateRange = validateDateRangeForNonPremium(dateRange, dayLimit);
        if (dateRange.start != null) {
          newFilters.start_time = `${dateRange.start.toString()}T00:00:00Z`;
        }
        if (dateRange.end != null) {
          newFilters.end_time = `${dateRange.end.toString()}T23:59:59Z`;
        }
        break;
      }
      case 'campaign_id': {
        // Handle campaign_id filter - only applicable for CampaignAction object type
        const filterValueStr: string = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsedCampaignId = Number.parseInt(filterValueStr, 10);
        if (!Number.isNaN(parsedCampaignId)) {
          newFilters.campaign_id = parsedCampaignId;
        }
        break;
      }
    }
  }

  return newFilters;
}

/**
 * Initialize filter values from existing filters.
 */
export function initializeFilterValues(filters: AuditLogFilters, dayLimit: number | null): FilterValue[] {
  const values: FilterValue[] = [];

  // Always-visible filter: Date Range (default: last 30 days)
  const startTimeStr: string | undefined = typeof filters.start_time === 'string' ? filters.start_time : undefined;
  const endTimeStr: string | undefined = typeof filters.end_time === 'string' ? filters.end_time : undefined;
  const hasStartTime = typeof startTimeStr === 'string' && startTimeStr.trim() !== '';
  const hasEndTime = typeof endTimeStr === 'string' && endTimeStr.trim() !== '';
  if (hasStartTime || hasEndTime) {
    let dateRangeValue: { start?: CalendarDate; end?: CalendarDate } = {};
    if (hasStartTime && startTimeStr !== undefined) {
      try {
        const dateStr = startTimeStr.split('T')[0];
        dateRangeValue.start = parseDate(dateStr);
      } catch {
        // ignore invalid date
      }
    }
    if (hasEndTime && endTimeStr !== undefined) {
      try {
        const dateStr = endTimeStr.split('T')[0];
        dateRangeValue.end = parseDate(dateStr);
      } catch {
        // ignore invalid date
      }
    }
    if (dateRangeValue.start != null || dateRangeValue.end != null) {
      // Validate and clamp date range for accounts with limited access
      dateRangeValue = validateDateRangeForNonPremium(dateRangeValue, dayLimit);
      values.push({ key: 'date_range', value: dateRangeValue });
    } else {
      values.push({ key: 'date_range', value: getDefaultDateRange() });
    }
  } else {
    // Default to last 30 days
    values.push({ key: 'date_range', value: getDefaultDateRange() });
  }

  // Always-visible filter: User (default: all users)
  if (typeof filters.user_id === 'number') {
    values.push({ key: 'user_id', value: filters.user_id.toString() });
  } else {
    values.push({ key: 'user_id', value: ALL_USERS_VALUE });
  }

  // Always-visible filter: Object Type (default: all object types)
  const objectTypeStr: string | undefined = typeof filters.object_type === 'string' ? filters.object_type : undefined;
  if (typeof objectTypeStr === 'string' && objectTypeStr.trim() !== '') {
    const actionStr: string | undefined = typeof filters.action === 'string' ? filters.action : undefined;
    const combinedValue =
      typeof actionStr === 'string' && actionStr.trim() !== '' ? `${objectTypeStr}:${actionStr}` : objectTypeStr;
    values.push({ key: 'object_type', value: combinedValue });
  } else {
    values.push({ key: 'object_type', value: ALL_OBJECT_TYPES_VALUE });
  }

  // Other filters (only added when they have values)
  const ipAddrStr: string | undefined = typeof filters.ip_addr === 'string' ? filters.ip_addr : undefined;
  if (typeof ipAddrStr === 'string' && ipAddrStr.trim() !== '') {
    values.push({ key: 'ip_addr', value: ipAddrStr });
  }
  const objectIdStr: string | undefined = typeof filters.object_id === 'string' ? filters.object_id : undefined;
  if (typeof objectIdStr === 'string' && objectIdStr.trim() !== '') {
    values.push({ key: 'object_id', value: objectIdStr });
  }
  const attributeNameStr: string | undefined =
    typeof filters.attribute_name === 'string' ? filters.attribute_name : undefined;
  if (typeof attributeNameStr === 'string' && attributeNameStr.trim() !== '') {
    values.push({ key: 'attribute_name', value: attributeNameStr });
  }
  // Campaign ID filter (only applicable for CampaignAction object type)
  if (typeof filters.campaign_id === 'number') {
    values.push({ key: 'campaign_id', value: filters.campaign_id.toString() });
  }

  return values;
}

/**
 * Validate and clamp date range in AuditLogFilters for accounts with limited access.
 * This ensures API calls respect the day limit even when filters come from URL parameters.
 *
 * @param filters - The filters to validate
 * @param dayLimit - The maximum number of days allowed (null means unlimited)
 * @returns A new filters object with validated/clamped dates (never mutates input)
 */
export function validateFiltersForNonPremium(filters: AuditLogFilters, dayLimit: number | null): AuditLogFilters {
  // Accounts with unlimited access have no restrictions - but still return new object for consistency
  if (dayLimit == null) {
    return { ...filters };
  }

  // If no date filters, return new object (never mutate input)
  const startTimeStr: string | undefined = typeof filters.start_time === 'string' ? filters.start_time : undefined;
  const endTimeStr: string | undefined = typeof filters.end_time === 'string' ? filters.end_time : undefined;
  const hasStartTime = typeof startTimeStr === 'string' && startTimeStr.trim() !== '';
  const hasEndTime = typeof endTimeStr === 'string' && endTimeStr.trim() !== '';

  if (!hasStartTime && !hasEndTime) {
    return { ...filters };
  }

  // Parse string dates to CalendarDate
  const dateRange: { start?: CalendarDate; end?: CalendarDate } = {};
  if (hasStartTime && startTimeStr !== undefined) {
    try {
      const dateStr = startTimeStr.split('T')[0];
      dateRange.start = parseDate(dateStr);
    } catch {
      // Invalid date - will be handled by validation
    }
  }
  if (hasEndTime && endTimeStr !== undefined) {
    try {
      const dateStr = endTimeStr.split('T')[0];
      dateRange.end = parseDate(dateStr);
    } catch {
      // Invalid date - will be handled by validation
    }
  }

  // Validate the date range
  const validatedRange = validateDateRangeForNonPremium(dateRange, dayLimit);

  // Convert back to string format and return new filters object
  const validatedFilters: AuditLogFilters = { ...filters };
  validatedFilters.start_time =
    validatedRange.start != null ? `${validatedRange.start.toString()}T00:00:00Z` : undefined;
  validatedFilters.end_time = validatedRange.end != null ? `${validatedRange.end.toString()}T23:59:59Z` : undefined;

  return validatedFilters;
}
